import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, IconButton, Popover, Tooltip, styled } from '@shared/ui';
import { MailIcon, OpenInNewIcon, ContentCopyIcon } from '@shared/ui/icons';
import { useDispatch, useSelector } from 'react-redux';
import { enqueueSnackbar, SNACKBAR_VARIANTS } from 'redux_store';
import copy from 'copy-to-clipboard';
import cx from 'classnames';
import Root, { classes } from './InfoRow.styles';

const EMAIL_HANDLER_GUIDE_URL =
  'https://support.google.com/chrome/thread/57026170/how-to-add-gmail-as-default-mailto-handler?hl=en';

const PopoverBody = styled('div')({
  padding: 16,
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  width: 280,
});

const PopoverTitle = styled('span')({
  fontSize: 15,
  fontWeight: 600,
  color: 'var(--qhs-text-1)',
});

const PopoverText = styled('span')({
  fontSize: 13,
  color: 'var(--qhs-text-2)',
  lineHeight: 1.5,
});

const PopoverHint = styled('p')({
  margin: '2px 0 0',
  fontSize: 11,
  color: 'var(--qhs-text-3)',
  lineHeight: 1.5,
});

const HintLink = styled('span')({
  color: 'var(--qhs-brand)',
  cursor: 'pointer',
  textDecoration: 'underline',
});

/**
 * Single info row inside InfoPane. Renders a 3-column grid:
 *   [label] [value(s)] [trail icon]
 *
 * Preserved behaviours from the legacy `_legacy/components/InfoPane/widgets/InfoRow.jsx`:
 *   - Click-to-copy with snackbar feedback (copy library, customCopyValue override).
 *   - 1.5 s yellow flash when the `info` prop changes (skips initial mount).
 *   - Email action variant opens a Popover with mailto + Outlook deeplink buttons.
 *   - Multi-line `info` (array) renders one line per element (used for address).
 *   - No-data row disables the click target and hides the trail icon.
 */
const InfoRow = ({
  title,
  info,
  mono = false,
  multiline = false,
  // eslint-disable-next-line no-unused-vars
  badge = false, // accepted for forward-compat; rendered as plain text for 1:1 with legacy
  tone, // 'critical' renders the value as a status-tone pill
  action,
  dataActionTooltipTitle,
  customCopyValue,
  noFlash = false,
}) => {
  const dispatch = useDispatch();
  const { infoPane, notifications } = useSelector(state => state.i18n);

  // Stable key for the flash effect — `info` can be an array (address
  // lines) that is recreated on every render even when its content is
  // unchanged. Without serialisation the effect dep changes on every
  // parent re-render and triggers a spurious yellow flash.
  const infoKey = useMemo(
    () => (Array.isArray(info) ? info.join('\n') : String(info ?? '')),
    [info]
  );

  // Flash on info change (skip initial mount). Suppressed entirely when
  // `noFlash` is set (Patient pane rows opt out — only Metrics rows want
  // the change indicator).
  const [flash, setFlash] = useState(false);
  const didMount = useRef(false);
  useEffect(() => {
    if (!didMount.current) {
      didMount.current = true;
      return undefined;
    }
    if (noFlash) return undefined;
    setFlash(true);
    const t = setTimeout(() => setFlash(false), 1500);
    return () => clearTimeout(t);
  }, [infoKey, noFlash]);

  const hasData = useMemo(
    () => info && info.toString().replaceAll(' ', '').replaceAll('-', '').length > 0,
    [info]
  );
  const isAction = action === 'email' && hasData;

  // Click-to-copy.
  const handleCopy = async () => {
    try {
      await copy(customCopyValue ?? info);
      dispatch(
        enqueueSnackbar({
          message: notifications.dataCopied,
          options: {
            variant: SNACKBAR_VARIANTS.success,
            anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
          },
        })
      );
    } catch (_) {
      dispatch(
        enqueueSnackbar({
          message: notifications.dataNotCopied,
          options: {
            variant: SNACKBAR_VARIANTS.error,
            anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
          },
        })
      );
    }
  };

  // Email popover. The shared Popover positions itself from its own
  // `trigger` element (it is NOT the MUI anchorEl API — passing anchorEl
  // silently rendered nothing, which is how the mail icon went dead).
  const [emailOpen, setEmailOpen] = useState(false);
  const handlePopoverOpen = e => {
    e.preventDefault();
    e.stopPropagation();
    setEmailOpen(open => !open);
  };
  const handlePopoverClose = e => {
    e?.stopPropagation?.();
    setEmailOpen(false);
  };

  const renderValue = () => {
    if (Array.isArray(info)) {
      return info.map(line => (
        <span key={line} className={cx(classes.value, classes.valueMultiline)}>
          {line}
        </span>
      ));
    }
    return (
      <span
        className={cx(classes.value, {
          [classes.valueMono]: mono,
          [classes.valueMuted]: !hasData,
          [classes.valueMultiline]: multiline,
          [classes.valuePill]: hasData && !!tone,
          [classes.valuePillCritical]: hasData && tone === 'critical',
        })}
      >
        {hasData ? info : '—'}
      </span>
    );
  };

  return (
    <Root>
      <div
        className={cx(classes.row, {
          [classes.rowAction]: isAction,
          [classes.rowNoData]: !hasData,
          [classes.rowFlash]: flash,
        })}
        onClick={hasData && !isAction ? handleCopy : undefined}
      >
        <span className={classes.label}>{title}</span>
        <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>{renderValue()}</span>
        <span className={cx(classes.trail, 'trail')}>
          {isAction ? (
            <Popover
              open={emailOpen}
              onClose={handlePopoverClose}
              placement="bottom-end"
              trigger={
                <span style={{ display: 'inline-flex' }}>
                  <Tooltip title={dataActionTooltipTitle}>
                    <IconButton size="small" onClick={handlePopoverOpen}>
                      <MailIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </span>
              }
            >
              <PopoverBody>
                <PopoverTitle>{infoPane.emailPatient}</PopoverTitle>
                <PopoverText>{infoPane.openEmailApp}</PopoverText>
                <Button
                  variant="secondary"
                  size="sm"
                  fullWidth
                  endIcon={<MailIcon fontSize="small" />}
                  onClick={e => {
                    handlePopoverClose(e);
                    // Encoded: an address carrying '?bcc=…&body=…' (the API
                    // only checks for one '@' and a dot) must not prefill the
                    // compose window with hidden recipients.
                    window.location.href = `mailto:${encodeURIComponent(info)}`;
                  }}
                >
                  {infoPane.emailPatientWithApp}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  fullWidth
                  endIcon={<OpenInNewIcon fontSize="small" />}
                  onClick={e => {
                    handlePopoverClose(e);
                    const url = `https://outlook.office.com/mail/deeplink/compose?to=${encodeURIComponent(
                      info
                    )}&subject=&body=`;
                    window.open(url, '_blank', 'noopener,noreferrer');
                  }}
                >
                  {infoPane.emailPatientWithOutlook}
                </Button>
                <PopoverHint>
                  {infoPane.emailHandlerNote}{' '}
                  <HintLink
                    role="button"
                    tabIndex={0}
                    onClick={() => window.open(EMAIL_HANDLER_GUIDE_URL, '_blank')}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        window.open(EMAIL_HANDLER_GUIDE_URL, '_blank');
                      }
                    }}
                  >
                    {infoPane.emailHandlerLink}
                  </HintLink>
                </PopoverHint>
              </PopoverBody>
            </Popover>
          ) : hasData ? (
            <ContentCopyIcon fontSize="small" />
          ) : null}
        </span>
      </div>
    </Root>
  );
};

export default InfoRow;
